import { useEffect, useMemo, useState } from 'react';
import { useAppSelector } from '../../hooks/redux';
import { useApi } from '../../hooks/useApi';
import { useCompanies } from '../../features/company/CompanyContext';
import api from '../../services/api';
import MessageThread from '../../components/MessageThread';
import NoCompany from '../../components/NoCompany';
import CompanyLogo from '../../components/CompanyLogo';
import { EmptyState, Loading, Notice, StatusPill } from '../../components/ui';
import { COMPANY_STATUS, companyDisplayName, companyTitle, timeAgo } from '../../lib/format';
import type { CompanySummary, Message, PersonRef } from '../../types/workspace';

type LastMessage = {
  id: string;
  body: string;
  kind: 'MESSAGE' | 'SYSTEM';
  createdAt: string;
  author: { id: string; name: string | null; email: string; role: string } | null;
} | null;

type InboxCompany = CompanySummary & { lastMessage?: LastMessage };

type Inbox = { companies: InboxCompany[] };

type Conversation = {
  company: {
    id: string;
    name: string;
    logicalName: string | null;
    workspaceLabel: string | null;
    hasLogo?: boolean;
    status?: string;
    assignedAdmin: PersonRef | null;
  };
  messages: Message[];
};

function previewText(msg: LastMessage): string {
  if (!msg) return 'No messages yet — say hello to your analyst';
  if (msg.kind === 'SYSTEM') return msg.body;
  const who = msg.author?.name || msg.author?.email || 'Someone';
  const body = msg.body.replace(/\s+/g, ' ').trim();
  return `${who}: ${body.length > 72 ? `${body.slice(0, 72)}…` : body}`;
}

export default function Messages() {
  const user = useAppSelector((s) => s.auth.user);
  const { activeId, select, loading: companiesLoading } = useCompanies();
  const [query, setQuery] = useState('');
  const [mobileThread, setMobileThread] = useState(Boolean(activeId));
  const inboxApi = useApi<Inbox>('/customer/companies?messages=1', 15000);
  const conversationApi = useApi<Conversation>(activeId ? '/customer/messages' : null, 10000);

  const companies = inboxApi.data?.companies || [];

  const sorted = useMemo(() => {
    return [...companies].sort((a, b) => {
      const at = a.lastMessage?.createdAt || a.updatedAt || '';
      const bt = b.lastMessage?.createdAt || b.updatedAt || '';
      return bt.localeCompare(at);
    });
  }, [companies]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sorted;
    return sorted.filter((c) =>
      [c.logicalName, c.name, c.workspaceLabel, c.targetLender, c.countryCode].some((v) => v?.toLowerCase().includes(q)),
    );
  }, [sorted, query]);

  useEffect(() => {
    if (!activeId && sorted.length) {
      select(sorted[0].id);
      setMobileThread(true);
    }
  }, [sorted, activeId, select]);

  const pickCompany = (id: string) => {
    if (id !== activeId) select(id);
    setMobileThread(true);
  };

  if (companiesLoading || inboxApi.loading) return <Loading />;
  if (!companies.length || conversationApi.code === 'NO_COMPANY') return <NoCompany />;
  if (inboxApi.error) return <Notice tone="rose">{inboxApi.error}</Notice>;

  const active = conversationApi.data?.company;
  const title = active ? companyTitle(active) : '';
  const analyst = active?.assignedAdmin;
  const analystName = analyst?.name || analyst?.email || 'Your Micus analyst';
  const status = active?.status ? COMPANY_STATUS[active.status] || { label: active.status, tone: 'slate' as const } : null;

  return (
    <div className="-mx-5 -mb-6 flex h-[calc(100dvh-13rem)] min-h-[480px] flex-col sm:-mx-8 sm:-mb-7">
      <div className="flex min-h-0 flex-1 overflow-hidden rounded-2xl border border-[#fce7f3] bg-white shadow-[0_12px_40px_rgba(107,0,48,0.06)]">
        <aside className={`${mobileThread ? 'hidden lg:flex' : 'flex'} w-full shrink-0 flex-col border-r border-[#fce7f3] lg:w-[300px] xl:w-[340px]`}>
          <div className="shrink-0 border-b border-[#fce7f3] px-4 py-4">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#c084a0]">Workspace</p>
            <h1 className="mt-0.5 text-xl font-black tracking-tight text-[#6b0030]">Messages</h1>
            <p className="mt-1 text-xs text-[#b0708f]">One thread per company with your Micus analyst.</p>
            <label className="sr-only" htmlFor="musb-message-search">Search conversations</label>
            <input
              id="musb-message-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search companies…"
              className="mt-3 w-full rounded-xl border border-[#fce7f3] bg-[#fffafc] px-3 py-2 text-sm text-[#4a1a30] outline-none transition focus:border-[#d6006e] focus:bg-white"
            />
          </div>

          {visible.length === 0 ? (
            <div className="p-4">
              <EmptyState title="No matches" body="Try another Micus company name." />
            </div>
          ) : (
            <ul className="min-h-0 flex-1 overflow-y-auto py-1" role="listbox" aria-label="Company conversations">
              {visible.map((company) => {
                const selected = company.id === activeId;
                const display = companyTitle(company);
                const last = company.lastMessage;
                return (
                  <li key={company.id}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={selected}
                      onClick={() => pickCompany(company.id)}
                      className={`flex w-full items-start gap-3 px-4 py-3 text-left transition ${
                        selected ? 'bg-[#fdf2f8]' : 'hover:bg-[#fffafc]'
                      }`}
                      style={selected ? { boxShadow: 'inset 3px 0 0 #d6006e' } : undefined}
                    >
                      <CompanyLogo
                        companyId={company.id}
                        name={companyDisplayName(company)}
                        hasLogo={company.hasLogo}
                        className="mt-0.5 h-10 w-10 rounded-xl text-sm"
                      />
                      <span className="min-w-0 flex-1">
                        <span className="flex items-baseline justify-between gap-2">
                          <span className={`truncate text-sm ${selected ? 'font-black text-[#d6006e]' : 'font-bold text-[#6b0030]'}`}>{display}</span>
                          <span className="shrink-0 text-[10px] font-medium text-[#c084a0]">{timeAgo(last?.createdAt || company.updatedAt)}</span>
                        </span>
                        <span className="mt-0.5 block truncate text-[11px] text-[#b0708f]">{previewText(last || null)}</span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </aside>

        <section className={`${mobileThread ? 'flex' : 'hidden lg:flex'} min-w-0 flex-1 flex-col`}>
          {!activeId ? (
            <div className="flex flex-1 items-center justify-center p-8">
              <EmptyState title="Pick a conversation" body="Choose a company on the left to message your Micus analyst." />
            </div>
          ) : conversationApi.loading && !conversationApi.data ? (
            <Loading />
          ) : conversationApi.error || !conversationApi.data || !active ? (
            <div className="p-5">
              <Notice tone="rose">{conversationApi.error || 'Could not load messages.'}</Notice>
            </div>
          ) : (
            <>
              <header className="relative flex shrink-0 items-center gap-3 overflow-hidden border-b border-[#fce7f3] px-4 py-3 sm:px-5">
                <div
                  className="pointer-events-none absolute inset-0 opacity-90"
                  style={{ background: 'linear-gradient(120deg, #fff5f8 0%, #ffffff 55%, #fdf2f8 100%)' }}
                  aria-hidden
                />
                <button
                  type="button"
                  className="relative rounded-lg px-2 py-1.5 text-xs font-bold text-[#d6006e] hover:bg-[#fdf2f8] lg:hidden"
                  onClick={() => setMobileThread(false)}
                >
                  ← Back
                </button>
                <CompanyLogo
                  companyId={active.id}
                  name={companyDisplayName(active)}
                  hasLogo={active.hasLogo}
                  className="relative h-11 w-11 rounded-xl text-base"
                />
                <div className="relative min-w-0 flex-1">
                  <h2 className="truncate text-base font-black text-[#6b0030]">{title}</h2>
                  <p className="truncate text-xs text-[#b0708f]">
                    With <span className="font-semibold text-[#6b0030]">{analystName}</span>
                  </p>
                </div>
                {status && <StatusPill label={status.label} tone={status.tone} />}
                <span className="relative hidden h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#d6006e] text-sm font-black text-white sm:inline-flex">
                  {(analyst?.name || analyst?.email || 'M').slice(0, 1).toUpperCase()}
                </span>
              </header>
              <MessageThread
                messages={conversationApi.data.messages}
                currentUserId={user?.id}
                placeholder="Message your Micus analyst…"
                peerLabel={analystName}
                onSend={async (body) => {
                  await api.post('/customer/messages', { body });
                  await Promise.all([conversationApi.reload(true), inboxApi.reload(true)]);
                }}
              />
            </>
          )}
        </section>
      </div>
    </div>
  );
}
